import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Info, Target} from '../../icons';
import {Spinner} from '../../Spinner';
import {BudgetDistributionTooltip} from './BudgetDistributionTooltip';
import {
    alertLevelSeverity,
    dayOfPeriodLabel,
    formatCompactAmount,
    formatCompactMoney,
    formatGoalValue,
    projectionSeverity,
} from './panelFormatters';
import {GOAL_OFF_TRACK, GOAL_ON_TRACK, goalsSeverity, goalsSummary} from './panelGoals';
import {clientAlertsPath} from './alerts/clientAlertsPaths';
import {anchorAttr} from '../../../guides/anchors';
import './KpiStrip.scss';

// Cuántos objetivos se nombran dentro del tile. Dos: el tile es un titular con su evidencia, no la
// lista de objetivos del cliente (esa vive en Monitoreo, a un click). Con tres ya empuja la altura
// de toda la fila de KPIs.
const GOALS_PREVIEW = 2;

// `dayOfPeriodLabel` es puro y no conoce el copy: se le pasa un `t` envuelto con el default para
// que la etiqueta nunca muestre la key cruda (la clave ya vive en es/en.json; el default es la red).
function withDayDefault(t) {
    return (key, params) => t(key, {...params, defaultValue: 'día {{day}} de {{days}}'});
}

function formatDecimal(value, digits = 1) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return number.toLocaleString('es-AR', {maximumFractionDigits: digits});
}

/**
 * Tira de KPIs del mes (`panel.kpis`): inversión a la fecha contra el esperado —con hacia dónde
 * cierra—, presupuesto, objetivos y alertas activas. No hace fetch propio.
 *
 * Los cuatro tiles muestran los MISMOS números en un cliente nuevo que en uno en régimen; lo que
 * cambia con `is_new_client` es el subtítulo, que pasa de comparativo ("esperado a hoy: X") a
 * pedagógico ("los últimos 30 días ya están sincronizados"). El día 1 no hay contra qué comparar,
 * y un "0 de lo esperado" leería como un problema cuando es sólo que todavía no arrancó.
 *
 * **La proyección de cierre vive DENTRO del tile de inversión** (feedback de Pablo sobre el mockup
 * v2): no es una cuarta magnitud del mes, es la misma inversión leída hacia adelante — "vas 250.000
 * y vas a cerrar en 4,1M" es una sola frase, y partirla en dos tiles obligaba a mirar de a uno para
 * entenderla. El lugar que libera lo ocupan los **objetivos**, que hasta ahora sólo se veían fila
 * por fila: con más de seis líneas el panel ni siquiera los mostraba todos, así que un objetivo
 * fuera de ritmo podía no estar en ninguna parte de la pantalla.
 */
export function KpiStrip({data, loading, clientId, currency, isNewClient, lines, onNewLine}) {
    const {t} = useTranslation();

    if (loading) {
        return (
            <div className="pnl-kpis">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    if (!data) return null;

    const month = data.month || {};
    const investment = data.investment || {};
    const budget = data.budget || {};
    const projection = data.projection || {};
    const alerts = data.active_alerts || {};

    const dayLabel = dayOfPeriodLabel(month.day, month.days_in_month, withDayDefault(t));

    // --- 1. Inversión del mes -----------------------------------------------------------------
    const renderInvestment = () => {
        const spend = formatCompactMoney(currency, investment.spend_to_date);
        const expected = formatCompactMoney(currency, investment.expected_to_date);
        // El esperado puede salir de MENOS líneas que las que tiene el cliente (las demás no tienen
        // dato fresco): el número no es comparable de frente y hay que decirlo.
        const partial = Number(investment.expected_lines) < Number(investment.total_lines);
        const shared = Number((investment.scope || {}).campaigns_shared || 0);
        // Cliente sin NINGUNA línea de control (recién creado, nada configurado): no hay esperado que
        // calcular ni ritmo que ubicar. "sin datos recientes" ahí se lee como una sincronización rota
        // —y no lo está—, y el "día 31 de 31" no dice nada sin una línea contra la cual medirlo. El
        // "sin datos recientes" queda para lo que de verdad nombra: líneas que existen y no tienen
        // BPI fresca.
        const noLines = Number(budget.lines_count || 0) === 0;

        return (
            <div className="pnl-kpi">
                <span className="pnl-kpi__label">
                    {t('client_panel.kpi_investment', 'Inversión del mes')}
                    {!noLines && renderScopeTooltip()}
                </span>
                <span className="pnl-kpi__value">{spend || '—'}</span>
                <span className="pnl-kpi__sub">
                    {noLines && (
                        <span className="pnl-kpi__muted">
                            {t('client_panel.kpi_investment_no_lines', 'todavía no hay líneas de seguimiento')}
                        </span>
                    )}
                    {!noLines && isNewClient && (
                        t('client_panel.kpi_investment_new', 'los últimos 30 días ya están sincronizados')
                    )}
                    {!noLines && !isNewClient && (
                        <>
                            {expected ? (
                                <>
                                    {t('client_panel.kpi_investment_expected', 'esperado a hoy:')}
                                    {' '}
                                    <strong>{expected}</strong>
                                    {partial && renderPartialTooltip()}
                                </>
                            ) : (
                                <span className="pnl-kpi__muted">
                                    {t('client_panel.kpi_investment_no_data', 'sin datos recientes')}
                                </span>
                            )}
                            {dayLabel ? ` · ${dayLabel}` : ''}
                            {shared > 0 && renderSharedTooltip(shared)}
                        </>
                    )}
                </span>
                {renderProjectionLine()}
            </div>
        );
    };

    // De dónde sale el número (card ZX1GJwJQ): Pablo no entendía por qué la inversión no coincidía
    // con lo que gastaron las cuentas. Es a propósito —se mide sólo lo que miden las líneas, sin
    // mezclar las cuentas sueltas («vamos con un solo lado», fede)— y hay que decirlo donde está el
    // número. Sin líneas no va: el subtítulo ya dice que no hay ninguna.
    // Va DENTRO del rótulo, y eso trae dos cosas que resuelve el SCSS (`pnl-kpi__label-tip`): el
    // rótulo es uppercase y el texto lo heredaba, y anclado al ícono el cartel abría hacia la
    // derecha, tapaba el «esperado a hoy» y se salía sobre el tile de Presupuesto (QA-pt-07).
    const renderScopeTooltip = () => (
        <span className="info-icon-container pnl-kpi__info pnl-kpi__info--label">
            <Info size={13} weight="bold"/>
            <span className="warning-tooltip pnl-kpi__label-tip">
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text">
                        {t('client_panel.kpi_investment_scope_tip')}
                    </span>
                </span>
            </span>
        </span>
    );

    // El esperado sale de las líneas que tienen evaluación FRESCA; las demás todavía no reportaron.
    // El copy nombra eso —lo que le pasa al cliente— y no "sobre cuántas líneas se calculó", que era
    // hablarle al usuario del funcionamiento interno del sync.
    const renderPartialTooltip = () => (
        <span className="info-icon-container pnl-kpi__info">
            <Info size={13} weight="bold"/>
            <span className="warning-tooltip">
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text">
                        {t('client_panel.kpi_investment_partial',
                            '{{count}} línea todavía no reportó datos de hoy',
                            {
                                count: Number(investment.total_lines) - Number(investment.expected_lines),
                                defaultValue_other: '{{count}} líneas todavía no reportaron datos de hoy',
                            })}
                    </span>
                </span>
            </span>
        </span>
    );

    /**
     * El aviso que sostiene el número. Desde que la inversión cuenta cada campaña UNA vez, el KPI ya
     * NO es la suma de las filas de abajo: un usuario que sume cuatro líneas a mano no va a llegar a
     * este total. Sin decir por qué, el fix de un bug genera el reporte del siguiente.
     */
    const renderSharedTooltip = (shared) => {
        const scope = investment.scope || {};
        // Sólo si de verdad sobraba plata: una campaña compartida que no gastó en el período deja
        // `overlap_excluded_amount` en 0, y `formatCompactMoney(currency, 0)` devuelve "ARS 0"
        // (string truthy) — el tooltip terminaba diciendo "sobrarían ARS 0".
        const duplicated = Number(scope.overlap_excluded_amount) > 0
            ? formatCompactMoney(currency, scope.overlap_excluded_amount)
            : null;
        return (
            <span className="info-icon-container pnl-kpi__info">
                <Info size={13} weight="bold"/>
                <span className="warning-tooltip -rich">
                    <span className="warning-tooltip-body">
                        <span className="warning-tooltip-text">
                            {/* Arranca por lo que le pasa al cliente —hay campañas repetidas— y no
                                por el inventario. Los conteos van con separador de miles: un "1827"
                                pelado al lado de un "ARS 270.982" se lee como dos formatos
                                distintos para lo mismo. */}
                            {t('client_panel.kpi_investment_shared',
                                '{{shared}} de las {{counted}} campañas están en más de una línea, y cada una se cuenta una sola vez.',
                                {
                                    counted: formatCompactAmount(scope.campaigns_counted),
                                    shared: formatCompactAmount(shared),
                                    interpolation: {escapeValue: false},
                                })}
                            {/* La cuenta que cierra el círculo: el usuario tiene las líneas a la
                                vista y puede querer sumarlas. Se nombra la acción y contra qué
                                comparar ("más que este total"); un "sobrarían X" suelto no dice
                                respecto de qué. `active_lines` y NO `total_lines`: este último
                                cuenta sólo las líneas que suman al presupuesto (las maximales),
                                mientras que las campañas se miden en TODAS las del cliente. */}
                            {duplicated && ` ${t('client_panel.kpi_investment_shared_amount',
                                'Sumar las {{lines}} líneas por separado daría {{amount}} más que este total.',
                                {
                                    lines: investment.active_lines || investment.total_lines,
                                    amount: duplicated,
                                    interpolation: {escapeValue: false},
                                })}`}
                        </span>
                    </span>
                </span>
            </span>
        );
    };

    /**
     * Qué hacer cuando el tile dice "Sin presupuesto".
     *
     * Hasta acá el número más importante del panel podía estar vacío y la pantalla no ofrecía
     * ninguna salida: había que saber que el presupuesto se carga adentro de una línea de control,
     * en otra pantalla. Pablo lo pidió como un lápiz que cargue "un presupuesto total a mano" y por
     * detrás arme una línea global; eso todavía no existe en el modelo, así que el atajo lleva al
     * flujo REAL —el mismo modal de siempre si no hay ninguna línea, o la vista de líneas YA
     * FILTRADA en las que no tienen monto (card gtAL5axI: el CTA no deja al usuario frente a 78
     * filas, lo deja frente a las que le faltan)—. Prometer un campo suelto que después no se
     * puede editar en ningún lado es peor que un click de más.
     */
    const renderBudgetCta = (linesCount) => {
        if (!linesCount) {
            // Sin una sola línea el presupuesto no tiene dónde vivir: primero se crea la línea.
            return (
                <button type="button" className="pnl-kpi__cta" onClick={onNewLine}>
                    {t('client_panel.kpi_budget_cta_new_line', 'Crear una línea y cargarlo')}
                </button>
            );
        }
        return (
            <Link className="pnl-kpi__cta" to={`/client/${clientId}/lines?filtro=sin-presupuesto`}>
                {t('client_panel.kpi_budget_cta_load', 'Cargar el presupuesto')}
            </Link>
        );
    };

    // --- 2. Presupuesto -----------------------------------------------------------------------
    const renderBudget = () => {
        const total = formatCompactMoney(currency, budget.total);
        const linesCount = Number(budget.lines_count || 0);
        const without = Number(budget.lines_without_budget || 0);

        // `lines_count` cuenta TODAS las líneas activas (el Panel lo usa para saber si el cliente
        // tiene alguna) y el árbol trae sólo las del presupuesto de ESTE mes: sin las eventuales
        // terminadas o futuras. Si difieren, «6 líneas» al lado de un monto que sale de 3 se leía
        // raro (QA-pt-01). Un backend viejo manda todas en el árbol y el texto queda como siempre.
        const monthLines = Array.isArray(budget.distribution) ? budget.distribution.length : linesCount;
        let lines = monthLines < linesCount
            ? t('client_panel.kpi_budget_lines_of_month', {count: monthLines, total: linesCount})
            : t('client_panel.kpi_budget_lines', {
                count: linesCount,
                defaultValue: '{{count}} línea de seguimiento',
                defaultValue_other: '{{count}} líneas de seguimiento',
            });
        if (without > 0) {
            lines += ` (${t('client_panel.kpi_budget_without', '{{count}} sin presupuesto', {count: without})})`;
        }
        const parts = [lines];
        if (budget.per_platform) {
            parts.push(t('client_panel.kpi_budget_per_platform', 'definido por plataforma'));
        }

        const hasBudget = Number(budget.total) > 0;

        return (
            <div className="pnl-kpi">
                <span className="pnl-kpi__label">{t('client_panel.kpi_budget', 'Presupuesto')}</span>
                <span className="pnl-kpi__value">
                    {hasBudget ? total : t('client_panel.kpi_budget_none', 'Sin presupuesto')}
                </span>
                {!hasBudget && renderBudgetCta(linesCount)}
                <span className="pnl-kpi__sub">
                    {isNewClient
                        ? t('client_panel.kpi_budget_new', 'sugerido desde el gasto real — edítalo cuando quieras')
                        : parts.join(' · ')}
                    {/* El ⓘ va SIEMPRE que haya líneas: el número no es reconstruible mirando la
                        pantalla ni cuando el total es la simple suma de 75 líneas. */}
                    <BudgetDistributionTooltip
                        distribution={budget.distribution}
                        currency={currency}
                        basis={budget.basis}
                        perPlatform={budget.per_platform}
                        linesWithoutBudget={budget.lines_without_budget}
                        partialOverlap={budget.partial_overlap}
                    />
                </span>
            </div>
        );
    };

    // --- Proyección de cierre (dentro del tile de inversión) ----------------------------------
    /**
     * La misma inversión, leída hacia adelante. Va como pie del tile —separada por una línea, no
     * como un tile propio— porque sin el gasto a la fecha al lado el número no significa nada.
     *
     * Conserva entera la semántica que costó afinar contra prod: el color sale de `status` y NUNCA
     * de recalcular la banda desde el porcentaje (las bandas las define el backend, y ya mordió
     * tener dos fuentes para el mismo veredicto), y sin nada computable dice "calculando" en gris
     * en vez de inventar un 0%.
     */
    const renderProjectionLine = () => {
        const amount = formatCompactMoney(currency, projection.amount);
        const pct = formatDecimal(projection.vs_budget_pct);
        const severity = projectionSeverity(projection.status);

        // Vale tanto para el día 1 como para un cliente viejo que dejó de sincronizar.
        const computing = !amount || pct === null;

        return (
            <span className="pnl-kpi__proj">
                <span className="pnl-kpi__proj-label">
                    {t('client_panel.kpi_projection', 'Proyección de cierre')}
                </span>
                {computing ? (
                    <span className="pnl-kpi__muted">
                        {t('client_panel.kpi_projection_computing', 'calculando')}
                    </span>
                ) : (
                    <>
                        <span className="pnl-kpi__proj-val">{`≈ ${amount}`}</span>
                        {isNewClient ? (
                            <span className="pnl-kpi__proj-note">
                                {` · ${t('client_panel.kpi_projection_new',
                                    '{{pct}}% — calculada con tu historial, se afina día a día', {pct})}`}
                            </span>
                        ) : (
                            <>
                                {' · '}
                                <strong className={severity ? `-${severity}` : ''}>
                                    {t('client_panel.kpi_projection_vs_budget', '{{pct}}% del presupuesto', {pct})}
                                </strong>
                                {/* "empuja el desvío" SÓLO cuando el cierre se proyecta pasado
                                    (`OVER`). Con ON_TRACK o UNDER el veredicto está en verde/rojo por
                                    otra razón y nombrar un desvío contradice al número de al lado ("96,3%
                                    del presupuesto — Fulano empuja el desvío"). El backend puede seguir
                                    mandando la línea; qué se muestra lo decide la pantalla. */}
                                {projection.status === 'OVER' && projection.top_deviation_line_name && ` — ${t(
                                    'client_panel.kpi_projection_top_deviation',
                                    '{{name}} empuja el desvío',
                                    {name: projection.top_deviation_line_name, interpolation: {escapeValue: false}},
                                )}`}
                            </>
                        )}
                    </>
                )}
            </span>
        );
    };

    // --- 3. Objetivos -------------------------------------------------------------------------
    /**
     * Cómo vienen los objetivos del cliente, sumados desde sus líneas (`lines[].goal`).
     *
     * Ocupa el lugar que dejó la proyección. Hasta ahora un objetivo sólo existía en la fila de su
     * línea, y la card corta en seis: un cliente con quince líneas podía tener un objetivo en rojo
     * que no aparecía en NINGÚN lado del panel. Acá el titular dice cuántos van en ritmo y las dos
     * filas de abajo muestran primero los que no.
     *
     * El denominador son los objetivos EVALUADOS, no los definidos: un objetivo cuya línea todavía
     * no sincronizó no está atrasado, es que no se sabe (`on_track: null`), y meterlo en la cuenta
     * acusaría un problema que nadie verificó.
     */
    const renderGoals = () => {
        const summary = goalsSummary(lines);
        const severity = goalsSeverity(summary);
        const linesPath = `/client/${clientId}/lines`;
        const hiddenGoals = Math.max(summary.items.length - GOALS_PREVIEW, 0);

        // Ninguna línea con objetivo: el tile ofrece la acción que falta en vez de un "0 de 0".
        // El CTA aterriza en la vista de líneas YA FILTRADA en las que no tienen objetivo (card
        // gtAL5axI): no deja al usuario frente a todas las filas, lo deja frente a las que faltan.
        if (!summary.defined) {
            return (
                <div className="pnl-kpi">
                    <span className="pnl-kpi__label">{t('client_panel.kpi_goals', 'Objetivos')}</span>
                    <span className="pnl-kpi__value pnl-kpi__value--empty">
                        <Target size={20}/>
                        {t('client_panel.kpi_goals_none', 'Sin definir')}
                    </span>
                    <span className="pnl-kpi__sub">
                        {Number(budget.lines_count || 0) > 0 ? (
                            <Link className="pnl-kpi__cta" to={`${linesPath}?filtro=sin-objetivo`}>
                                {t('client_panel.kpi_goals_cta', 'Definir un objetivo')}
                            </Link>
                        ) : (
                            <span className="pnl-kpi__muted">
                                {t('client_panel.kpi_goals_no_lines',
                                    'se definen dentro de cada línea de seguimiento')}
                            </span>
                        )}
                    </span>
                </div>
            );
        }

        return (
            <Link className="pnl-kpi pnl-kpi--link" to={linesPath}>
                <span className="pnl-kpi__label">{t('client_panel.kpi_goals', 'Objetivos')}</span>
                <span className={`pnl-kpi__value ${severity ? `-${severity}` : ''}`}>
                    {summary.evaluated > 0 ? (
                        <>
                            {t('client_panel.kpi_goals_on_track', '{{onTrack}} de {{total}}', {
                                onTrack: summary.onTrack,
                                total: summary.evaluated,
                            })}
                            <span className="pnl-kpi__value-note">
                                {t('client_panel.kpi_goals_on_track_note', 'en ritmo')}
                            </span>
                        </>
                    ) : (
                        // Definidos pero sin una sola evaluación: el número honesto es cuántos hay.
                        t('client_panel.kpi_goals_pending', {
                            count: summary.defined,
                            defaultValue: '{{count}} sin evaluar',
                            defaultValue_other: '{{count}} sin evaluar',
                        })
                    )}
                </span>
                <span className="pnl-kpi__sub">
                    {summary.items.slice(0, GOALS_PREVIEW).map((item) => renderGoalRow(item))}
                    {/* Card ZX1GJwJQ: con «0 de 4» arriba y dos filas abajo, las otras dos no
                        existían en ningún lado del tile. El titular cuenta los evaluados y la lista
                        trae también los sin evaluar, así que lo que falta se mide contra la lista. */}
                    {hiddenGoals > 0 && (
                        <span className="pnl-goal-row pnl-goal-row--more">
                            {t('client_panel.kpi_goals_more', {count: hiddenGoals})}
                        </span>
                    )}
                </span>
            </Link>
        );
    };

    // Una fila del tile: el punto de estado, la métrica y dónde está contra su meta. El nombre de
    // la LÍNEA no entra —no hay ancho— y por eso el tile linkea a la vista de líneas, donde cada
    // objetivo aparece con la suya.
    const renderGoalRow = (item) => {
        const goal = item.goal;
        const dotClass = item.state === GOAL_ON_TRACK ? '-green'
            : item.state === GOAL_OFF_TRACK ? '-gold' : '-gray';
        const current = formatGoalValue(goal.current_value);
        const target = formatGoalValue(goal.target_value);
        const direction = goal.direction === 'LTE' ? '≤' : '≥';

        return (
            <span className="pnl-goal-row" key={item.lineId}>
                <span className={`pnl-goal-row__dot ${dotClass}`}/>
                <span className="pnl-goal-row__metric">{goal.metric_label}</span>
                {current !== null && <strong className="pnl-goal-row__val">{current}</strong>}
                {target !== null && (
                    <span className="pnl-goal-row__target">
                        {t('client_panel.kpi_goals_target', 'de {{direction}} {{target}}', {
                            direction,
                            target,
                            interpolation: {escapeValue: false},
                        })}
                    </span>
                )}
            </span>
        );
    };

    // --- 4. Alertas vigentes ------------------------------------------------------------------
    const renderAlerts = () => {
        const count = Number(alerts.count || 0);
        const snoozed = Number(alerts.snoozed_count || 0);
        // El KPI nombra la MÁS VIEJA (la que viene arrastrándose), no la más reciente: el prompt del
        // momento ya cuelga de `latest`. Son dos elecciones distintas sobre la misma lista.
        // Fallback a `latest` porque el backend y este repo no deployan a la vez.
        //
        // UNA sola alerta gobierna el tile entero — color del número Y texto. Con dos dueñas
        // (el color de `latest`, el texto de `oldest`) el número quedaba del color de una alerta
        // que el tile no nombraba (visto en prod, Rouge 06/08 — hallazgo del review del Panel).
        const highlighted = alerts.oldest || alerts.latest;
        // Cero alertas SÍ es verde: acá el backend evaluó y no encontró nada (a diferencia de una
        // línea sin veredicto, que va a gris).
        const severity = count > 0 ? alertLevelSeverity(highlighted && highlighted.level) : 'green';
        // Card j61xJLod: llevaba a la Bitácora del AI Booster, donde las alertas vienen mezcladas con
        // todo lo que pasó en las plataformas y por ráfaga (más filas que este número). La pantalla
        // de Alertas del cliente lista exactamente los problemas que este tile cuenta.
        const to = clientAlertsPath(clientId);

        return (
            <Link className="pnl-kpi pnl-kpi--link" to={to}>
                <span className="pnl-kpi__label">
                    {isNewClient
                        ? t('client_panel.kpi_alerts_new_title', 'Alertas')
                        : t('client_panel.kpi_alerts', 'Alertas vigentes')}
                </span>
                <span className={`pnl-kpi__value ${isNewClient ? '' : `-${severity}`}`}>
                    {isNewClient ? t('client_panel.kpi_alerts_new_value', 'Activas') : count}
                </span>
                <span className="pnl-kpi__sub">
                    {isNewClient && t('client_panel.kpi_alerts_new_sub',
                        'las {{count}} líneas nacen con alerta de ritmo de presupuesto',
                        {count: Number(budget.lines_count || 0)})}
                    {!isNewClient && count > 0 && highlighted && (
                        <>
                            {/* Prefijo con corte («La más vieja: X»): como sufijo pegado a la frase
                                de la alerta se leía como una sola oración rota. Sólo cuando de
                                verdad es la más vieja — en el fallback a `latest` no se afirma. */}
                            {alerts.oldest && `${t('client_panel.kpi_alerts_oldest', 'La más vieja:')} `}
                            <span className={`-${alertLevelSeverity(highlighted.level)}`}>
                                {highlighted.label}
                            </span>
                        </>
                    )}
                    {!isNewClient && count === 0 && (
                        <>
                            {t('client_panel.kpi_alerts_none', 'Sin alertas vigentes')}
                            {snoozed > 0 && (
                                <span className="pnl-kpi__muted">
                                    {` · ${t('client_panel.kpi_alerts_snoozed', '{{count}} silenciadas', {count: snoozed})}`}
                                </span>
                            )}
                        </>
                    )}
                </span>
            </Link>
        );
    };

    return (
        <div className="pnl-kpis" {...anchorAttr('panel_kpis')}>
            {renderInvestment()}
            {renderBudget()}
            {renderGoals()}
            {renderAlerts()}
        </div>
    );
}
